'use client';

/**
 * Schoolhouse translations.
 *
 * common, nav, and app are in the first bundle. Any other namespace loads from
 * src/locales/en the first time one of its keys is translated.
 */
import { useSyncExternalStore } from 'react';

import type { TranslationKey } from '@/lib/i18n-messages';
import { criticalMessages, loadNamespaceModule } from '@/locales/loaders';

export type { TranslationKey } from '@/lib/i18n-messages';

export type TranslationValues = Readonly<Record<string, string | number>>;

const cache = new Map<string, string>();
const started = new Set<string>();
let version = 0;
const listeners = new Set<() => void>();

function flatten(namespace: string, value: unknown, prefix: string): void {
  if (typeof value === 'string') {
    cache.set(prefix === '' ? namespace : `${namespace}.${prefix}`, value);
    return;
  }
  if (typeof value !== 'object' || value === null) return;
  for (const [key, child] of Object.entries(value)) {
    flatten(namespace, child, prefix === '' ? key : `${prefix}.${key}`);
  }
}

function publish(): void {
  version += 1;
  for (const listener of listeners) listener();
}

for (const [namespace, value] of Object.entries(criticalMessages)) {
  started.add(namespace);
  flatten(namespace, value, '');
}

function ensure(namespace: string): void {
  if (namespace === '' || started.has(namespace)) return;
  const pending = loadNamespaceModule(namespace);
  if (pending === null) return;
  started.add(namespace);
  void pending.then((mod) => {
    flatten(namespace, mod.default, '');
    publish();
  });
}

function translate(key: TranslationKey, values?: TranslationValues): string {
  const dot = key.indexOf('.');
  ensure(dot === -1 ? key : key.slice(0, dot));
  const template = cache.get(key) ?? key;
  if (values === undefined) return template;
    return template.replaceAll(/\{\{(\w+)\}\}/g, (match: string, token: string): string => {
    const replacement = values[token];
    return replacement === undefined ? match : String(replacement);
  });
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): number {
  return version;
}

export type TranslateFn = (key: TranslationKey, values?: TranslationValues) => string;

export function useTranslation(): { readonly t: TranslateFn } {
  useSyncExternalStore(subscribe, snapshot, snapshot);
  return { t: translate };
}
